import React, { useState, useRef, useEffect } from 'react';
import {
	Box, Container, Paper, Stack, Typography, Checkbox,
	Tooltip,
} from '@mui/material';
import SignatureCanvas from '../../../components/SignaturePad';
import './styles.css';
import { ContainedButton } from '../../../components/ui/Buttons';
import { GetUserDetails, PostAgreementUpload } from '../../../services/auth.services';
import { useNavigate } from "react-router-dom";
import moment from 'moment';
import { PDFDocument } from "pdf-lib";
import { formatLabel } from '../../../utils/utilityFunctions';
import { parsePhoneNumberFromString } from 'libphonenumber-js';
import countryList from 'react-select-country-list';

function base64ToUint8Array(base64) {
	const binary = atob(base64);
	const len = binary.length;
	const bytes = new Uint8Array(len);
	for (let i = 0; i < len; i++) {
		bytes[i] = binary.charCodeAt(i);
	}
	return bytes;
}

function formatPhoneNumberByCountry(phone, countryName) {
	const countries = countryList().getData();
	const match = countries.find(
		(c) => c.label.toLowerCase() === countryName?.toLowerCase()
	);
	const countryCode = match ? match.value : 'US'; // fallback to US

	const phoneNumber = parsePhoneNumberFromString(phone, countryCode);
	return phoneNumber ? phoneNumber.formatInternational() : phone;
}


function ClientContract() {
	const [sellerSignature, setSellerSignature] = useState('');
	// const [isScrolledToBottom, setIsScrolledToBottom] = useState(false);
	const [userAgree, setUserAgree] = useState(false);
	const [userDetails, setUserDetails] = useState(null);
	const [documentStatus, setDocumentStatus] = useState('');
	const navigate = useNavigate();
	const hasRun = useRef(false);
	const [pdfUrl, setPdfUrl] = useState(null);
	const [pdfBlob, setPdfBlob] = useState(null);
	// const containerRef = useRef(null);

	useEffect(() => {
		if (hasRun.current) return;
		hasRun.current = true;

		if (!localStorage.getItem("uniqueId") || !localStorage.getItem("businessEncryptedId")) {
			navigate("/login")
		}

		const fetchData = async () => {
			try {
				await GetUserDetails(localStorage.getItem("uniqueId")).then((row) => {
					setUserDetails(row);
					switch (row.onboardingStatus) {
						case 'PENDING_DOCUMENT_APPROVAL':
							setDocumentStatus("PENDING_DOCUMENT_APPROVAL");
							break;
						case 'PENDING_BUSINESS_AGREEMENT':
							setDocumentStatus("PENDING_BUSINESS_AGREEMENT");
							break;
						case 'PENDING_FINAL_APPROVAL':
							setDocumentStatus("PENDING_FINAL_APPROVAL");
							break;
						case 'COMPLETE':
							setTimeout(() => navigate("/login"));
							break;
						default:
							setTimeout(() => navigate("/login"));
					}
				});
			} catch (error) {
				console.error('Error', error);;
			}
		};

		if (localStorage.getItem("uniqueId")) {
			fetchData();
		}
		// eslint-disable-next-line
	}, [])
	useEffect(() => {
		if (!userDetails) return;
		const formattedContactNumber = formatPhoneNumberByCountry(userDetails?.contactNo, userDetails?.country);

		async function fillPdf() {
			try {
				const pdfBytes = await fetch("/VePay_Maste_Sales_Agreement.pdf").then(res => res.arrayBuffer());

				// Load into pdf-lib
				const pdfDoc = await PDFDocument.load(pdfBytes);

				// Get form fields
				const form = pdfDoc.getForm();
				// Fill in fields by their exact names from PDFfiller
				form.getTextField("signingDate").setText(moment().format('L'));
				form.getTextField("sellerName").setText(userDetails?.firstName + ' ' + userDetails?.lastName);
				form.getTextField("sellerCountry").setText(userDetails?.country);
				form.getTextField("sellerIdNumber").setText(userDetails?.ownerId);
				form.getTextField("businessOwnerIdType").setText(formatLabel(userDetails?.businessOwnerIdType));
				form.getTextField("sellerAddress").setText(userDetails?.address);
				form.getTextField("sellerEmail").setText(userDetails?.email);
				form.getTextField("sellerAttention").setText(userDetails?.firstName + ' ' + userDetails?.lastName);
				form.getTextField("sellerPhone").setText(formattedContactNumber);
				form.getTextField("purchaser").setText('VePay LLC');
				form.getTextField("purchaserName").setText('VePay');
				form.getTextField("purchaserEmail").setText('legal@vepay.io');
				form.getTextField("purchaserAddress").setText('400 Rella Blvdste. 207-732 Montebello, NY 10901 United States');
				form.getTextField("purchaserAttention").setText('VePay Legal Team');
				form.getTextField("purchaserPhone").setText('+1 332 203 2313');
				// Flatten so fields become static text
				form.flatten();

				if (sellerSignature) {
					let image;
					if (sellerSignature.startsWith("data:image/png")) {
						const base64 = sellerSignature.split(",")[1];
						const bytes = base64ToUint8Array(base64);
						image = await pdfDoc.embedPng(bytes);
					} else if (
						sellerSignature.startsWith("data:image/jpeg") ||
						sellerSignature.startsWith("data:image/jpg")
					) {
						const base64 = sellerSignature.split(",")[1];
						const bytes = base64ToUint8Array(base64);
						image = await pdfDoc.embedJpg(bytes);
					} else if (sellerSignature.startsWith("data:image/svg+xml")) {
						const base64 = sellerSignature.split(",")[1];
						const svgText = atob(base64); // decode to string
						image = await pdfDoc.embedSvg(svgText);
					} else {
						throw new Error("Unsupported image format. Only PNG, JPG/JPEG, SVG are allowed.");
					}
					const page17 = pdfDoc.getPages()[17];
					const page20 = pdfDoc.getPages()[20];
					const page23 = pdfDoc.getPages()[23];
					const page24 = pdfDoc.getPages()[24];
					const imgDims = image.scaleToFit(130, 130);

					page17.drawImage(image, {
						x: 100,
						y: 350,
						width: imgDims.width,
						height: imgDims.height,
					});
					page20.drawImage(image, {
						x: 130,
						y: 350,
						width: imgDims.width,
						height: imgDims.height,
					});
					page23.drawImage(image, {
						x: 100,
						y: 200,
						width: imgDims.width,
						height: imgDims.height,
					});
					page24.drawImage(image, {
						x: 100,
						y: 410,
						width: imgDims.width,
						height: imgDims.height,
					});
				}

				// Create a blob and object URL
				const newPdfBytes = await pdfDoc.save();
				const blob = new Blob([newPdfBytes], { type: "application/pdf" });
				// Set state so it renders
				setPdfBlob(blob); // save for upload
				setPdfUrl(URL.createObjectURL(blob));
			} catch (error) {
				console.error("Error filling PDF:", error);
			}
		}

		fillPdf();
	}, [sellerSignature, userDetails]);


	const handleUpload = async () => {
		if (!pdfBlob) {
			alert("PDF not generated yet!");
			return;
		}

		try {

			const response = await PostAgreementUpload(pdfBlob);

			if (response.status === 200) {
				setDocumentStatus("PENDING_FINAL_APPROVAL");
			} else {
				console.error('Upload failed:', response);
			}
		} catch (error) {
			console.error('Error uploading PDF agreement:', error);
		}
	};


	// const handleScroll = () => {
	// 	const div = containerRef.current;
	// 	if (!div) return;

	// 	const isBottom =
	// 		div.scrollTop + div.clientHeight >= div.scrollHeight - 10;

	// 	setIsScrolledToBottom(isBottom);
	// };

	return (
		<Box>

			{documentStatus === 'PENDING_DOCUMENT_APPROVAL' ? (
				<Container maxWidth="md" sx={{ py: 4 }}>
					<Paper elevation={1} sx={{ p: 6, backgroundColor: '#f8fafc', border: '1px solid #e2e8f0' }}>
						<Typography variant="h3" sx={{ fontWeight: 600, mb: 3, color: '#1a365d', textAlign: 'center' }}>
							Application Under Review
						</Typography>
						<Typography variant="h5" sx={{ mb: 2, textAlign: 'center' }}>
							Thank you for completing your onboarding with VePay. Our team is currently reviewing your information and documents.
							Once the review is complete, we will notify you promptly via email, and your final contract will be made available for your signature.
						</Typography>
						<Typography variant="h5" sx={{ textAlign: 'center' }}>
							We appreciate your patience and look forward to welcoming you to VePay. If you have any questions, please contact our support team at <a href="mailto:support@vepay.io" style={{ fontWeight: "bold", textDecoration: "none", color: "inherit" }}>
								support@vepay.io
							</a>
						</Typography>

					</Paper>
				</Container>
			) : (
				documentStatus === 'PENDING_FINAL_APPROVAL' ? (
					<Container maxWidth="md" sx={{ py: 4 }}>
						<Paper elevation={1} sx={{ p: 6, backgroundColor: '#f8fafc', border: '1px solid #e2e8f0' }}>
							<Typography variant="h3" sx={{ fontWeight: 600, mb: 3, color: '#1a365d', textAlign: 'center' }}>
								You're Almost There!
							</Typography>
							<Typography variant="h5" sx={{ mb: 2, textAlign: 'center' }}>
								Thank you for completing the final step of your onboarding.
							</Typography>
							<Typography variant="h5" sx={{ mb: 2, textAlign: 'center' }}>
								Our team is reviewing your submission, and you’ll soon receive access to your VePay Client Portal and get funded.
							</Typography>
							<Typography variant="h5" sx={{ textAlign: 'center', mb: 3 }}>
								If you have any questions, feel free to reach out to us at <a href="mailto:support@vepay.io" style={{ fontWeight: "bold", textDecoration: "none", color: "inherit" }}>
									support@vepay.io
								</a>
							</Typography>
							<Box sx={{ display: 'flex', justifyContent: 'center' }}>
								<ContainedButton
									variant="contained"
									onClick={() => {
										localStorage.clear();
										navigate('/login');
									}}
								>
									Sign Out
								</ContainedButton>
							</Box>
						</Paper>
					</Container>
				) : (
					<Container maxWidth="lg" sx={{ py: 4 }}>
						<Box sx={{ mb: 4 }}>
							{pdfUrl ? (
								// <div
								// 	ref={containerRef}
								// 	onScroll={handleScroll}
								// 	style={{ width: "100%", height: "500px", overflowY: "auto" }}
								// >
								<iframe src={pdfUrl} width="100%" height="500px" title="User PDF" />
								// </div>

							) : (
								<p>Generating PDF...</p>
							)}

							{/* Signature Pad */}
							<br></br>
							<Paper elevation={1} sx={{ p: 4, mb: 4, border: '1px solid #e2e8f0' }} id="signatures">
								<SignatureCanvas onChange={setSellerSignature} />
							</Paper>

							{/* Agreement Note */}
							<Paper elevation={1} sx={{ p: 4, backgroundColor: '#f8fafc', border: '1px solid #e2e8f0' }}>
								<Typography variant="h6" sx={{ fontWeight: 600, mb: 3, color: '#1a365d', textAlign: 'center' }}>
									THIS AGREEMENT has been entered into on the date stated at the beginning of this Agreement.
								</Typography>
								<Stack direction="row">
									<Tooltip
										title="To enable the checkbox, scroll to the bottom of the document"
										placement="top-start">
										<span>
											<Checkbox
												// disabled={!isScrolledToBottom}
												checked={userAgree}
												onClick={() => setUserAgree(!userAgree)}
											/>
										</span>
									</Tooltip>
									<Typography variant="body2" sx={{ color: '#64748b', lineHeight: 1.6 }}>
										By signing above and checking the box, the Seller acknowledges that they have read, understood, and agree to be bound by all terms and conditions of this Master Sales Agreement.
									</Typography>
								</Stack>

							</Paper>
							<br></br>
							<Stack direction="row" spacing={2}>
								{/* <TextButton variant='outlined' onClick={downloadDocx}>Download DOCX</TextButton> */}
								<ContainedButton
									variant='contained'
									disabled={sellerSignature && userAgree ? false : true}
									onClick={handleUpload}
								>
									Submit Agreement
								</ContainedButton>
							</Stack>
						</Box>
					</Container>
				)


			)}


		</Box>
	);
}

export default ClientContract;
